import React from "react";
import * as ReactIcons from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import { ensureContrast } from "../utils/contrastColor";
import "../styles/TopbarNav.css";

// Desktop top-bar navigation. Renders the same `sidebarPages` the mobile bottom
// bar uses, so the two stay in sync. Shown on web only; hidden on mobile by CSS,
// where your existing bottom bar takes over. All colors come from useTheme.

type Page = {
    buttonText: string;
    url: string | string[];
    iconName: keyof typeof ReactIcons;
    hideInDesktopView?: boolean;
};

type Props = {
    sidebarPages: Page[];
};

const isExternal = (u: string) =>
    u.startsWith("http://") || u.startsWith("https://") || u.startsWith("www.");

const TopbarNav = (props: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme } = useTheme();

    const pages = (props.sidebarPages || []).filter((p) => !p.hideInDesktopView);
    if (pages.length === 0) return null;

    // Active / selected colour: the themed accent, but if it doesn't read
    // clearly against the top bar, tune it (same logic as the generated logo).
    const t = theme as any;
    const activeRaw =
        t.sidebarIconColorSelected ||
        t.categoryIndicatorColor ||
        t.primaryAccentColor ||
        t.topbarIconColor ||
        "";
    const bar = t.topbarBackgroundColor || "";
    const activeColor = activeRaw ? ensureContrast(activeRaw, bar, 3) : "inherit";

    const handleClick = (url: string | string[]) => {
        const target = typeof url === "string" ? url : url[0];
        if (isExternal(target)) {
            window.location.href = target.startsWith("http") ? target : `https://${target}`;
        } else {
            navigate(target);
        }
    };

    const isActive = (url: string | string[]) => {
        const path = location.pathname;
        return typeof url === "string" ? url === path : url.includes(path);
    };

    return (
        <nav
            className="topbar-nav"
            style={{
                ["--tn-text" as any]: theme.topbarIconColor || "inherit",
                ["--tn-active" as any]: activeColor,
            }}
        >
            {pages.map((page, index) => {
                const Icon = ReactIcons[page.iconName];
                const active = isActive(page.url);
                return (
                    <button
                        key={index}
                        type="button"
                        className={`topbar-nav-item ${active ? "active" : ""}`}
                        onClick={() => handleClick(page.url)}
                        aria-current={active ? "page" : undefined}
                    >
                        {Icon && (
                            <span className="tn-icon">
                                <Icon />
                            </span>
                        )}
                        <span className="tn-text">{page.buttonText}</span>
                    </button>
                );
            })}
        </nav>
    );
};

export default TopbarNav;